import {
  FIELD_TEXT_PATTERNS,
  HIDDEN_FIELD_PATTERNS,
  HIDDEN_DATE_PATTERNS,
  VALIDATION_MESSAGES,
} from "../constants/constants";

export interface ImcRelatedLinkIds {
  alturaLinkId: string | null;
  pesoLinkId: string | null;
  imcLinkId: string | null;
}

export interface PatientDataForPreFill {
  name?: string;
  age?: number;
  gender?: string;
  weight?: number;
  height?: number;
  birthDate?: string;
}

/** Percorre os itens do questionário e retorna os linkIds de Altura, Peso e IMC. */
export function getImcRelatedLinkIds(questionnaire: { item?: any[] }): ImcRelatedLinkIds {
  let alturaLinkId: string | null = null;
  let pesoLinkId: string | null = null;
  let imcLinkId: string | null = null;

  const walk = (items: any[] | undefined) => {
    if (!items) return;
    for (const item of items) {
      const text = (item.text || "").toLowerCase();
      if (FIELD_TEXT_PATTERNS.altura.some((p) => text.includes(p))) alturaLinkId = item.linkId;
      if (FIELD_TEXT_PATTERNS.peso.some((p) => text.includes(p))) pesoLinkId = item.linkId;
      if (FIELD_TEXT_PATTERNS.imc.some((p) => text.includes(p))) imcLinkId = item.linkId;
      if (item.item) walk(item.item);
    }
  };
  walk(questionnaire?.item);
  return { alturaLinkId, pesoLinkId, imcLinkId };
}

/**
 * Calcula IMC: peso (kg) / altura (m)².
 * Se altura > 3, trata como cm e converte para metros.
 */
export function calculateImc(pesoKg: number, alturaValue: number): number {
  const alturaMetros = alturaValue > 3 ? alturaValue / 100 : alturaValue;
  if (alturaMetros <= 0) return NaN;
  return pesoKg / (alturaMetros * alturaMetros);
}

/** Formata IMC com 2 casas decimais para exibição no campo. */
export function formatImcForField(imc: number): string {
  return String(Number(imc.toFixed(2)));
}

/** Valida um item do questionário conforme valor e regras do item. */
export function validateQuestionnaireField(item: any, value: any): string | undefined {
  if (
    item.required &&
    (!value || (Array.isArray(value) && value.length === 0))
  ) {
    return VALIDATION_MESSAGES.required;
  }

  if (
    value &&
    item.maxLength &&
    typeof value === "string" &&
    value.length > item.maxLength
  ) {
    return VALIDATION_MESSAGES.maxLength(item.maxLength);
  }

  if (value && item.type === "integer" && !Number.isInteger(Number(value))) {
    return VALIDATION_MESSAGES.integer;
  }

  if (value && item.type === "decimal" && Number.isNaN(Number(value))) {
    return VALIDATION_MESSAGES.decimal;
  }

  return undefined;
}

function patternMatchesAsWord(text: string, pattern: string): boolean {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`\\b${escaped}\\b`, "i").test(text);
}

export function shouldHideQuestionnaireItem(item: any): boolean {
  const text = (item.text || "").toLowerCase();
  if (HIDDEN_FIELD_PATTERNS.some((p) => patternMatchesAsWord(text, p))) return true;
  if (item.type === "date" && HIDDEN_DATE_PATTERNS.some((p) => patternMatchesAsWord(text, p)))
    return true;
  return false;
}

export function getPreFilledValue(item: any, patientData: PatientDataForPreFill): any {
  const text = (item.text || "").toLowerCase();

  if (FIELD_TEXT_PATTERNS.nome.some((p) => text.includes(p))) {
    return patientData.name ?? "";
  }

  if (FIELD_TEXT_PATTERNS.idade.some((p) => text.includes(p))) {
    return patientData.age?.toString() ?? "";
  }

  if (FIELD_TEXT_PATTERNS.peso.some((p) => text.includes(p))) {
    return patientData.weight?.toString() ?? "";
  }

  if (FIELD_TEXT_PATTERNS.altura.some((p) => text.includes(p))) {
    return patientData.height?.toString() ?? "";
  }

  if (FIELD_TEXT_PATTERNS.sexo.some((p) => text.includes(p))) {
    if (item.type === "choice" || item.type === "coding") {
      const genderValue = patientData.gender;
      if (genderValue === "male") {
        const maleOption = item.answerOption?.find(
          (option: any) =>
            option.valueCoding?.display?.toLowerCase().includes("masculino") ||
            option.valueCoding?.display?.toLowerCase().includes("male") ||
            option.valueString?.toLowerCase().includes("masculino") ||
            option.valueString?.toLowerCase().includes("male")
        );
        return maleOption?.valueCoding?.code ?? maleOption?.valueString ?? "";
      }
      if (genderValue === "female") {
        const femaleOption = item.answerOption?.find(
          (option: any) =>
            option.valueCoding?.display?.toLowerCase().includes("feminino") ||
            option.valueCoding?.display?.toLowerCase().includes("female") ||
            option.valueString?.toLowerCase().includes("feminino") ||
            option.valueString?.toLowerCase().includes("female")
        );
        return femaleOption?.valueCoding?.code ?? femaleOption?.valueString ?? "";
      }
    }
    if (patientData.gender === "male") return "masculino";
    if (patientData.gender === "female") return "feminino";
    if (patientData.gender === "other") return "outro";
    return patientData.gender ?? "";
  }

  if (FIELD_TEXT_PATTERNS.dataNascimento.some((p) => text.includes(p))) {
    return patientData.birthDate ?? "";
  }

  return "";
}

export function buildPreFilledAnswers(
  questionnaire: { item?: any[] },
  patientData: PatientDataForPreFill
): Record<string, any> {
  const preFilledAnswers: Record<string, any> = {};

  const preFillItems = (items: any[]) => {
    items.forEach((item) => {
      const value = getPreFilledValue(item, patientData);
      if (value) preFilledAnswers[item.linkId] = value;
      if (item.item) preFillItems(item.item);
    });
  };

  if (questionnaire?.item) preFillItems(questionnaire.item);

  const { alturaLinkId, pesoLinkId, imcLinkId } = getImcRelatedLinkIds(questionnaire);
  if (imcLinkId && alturaLinkId && pesoLinkId) {
    const alturaNum = Number(preFilledAnswers[alturaLinkId]);
    const pesoNum = Number(preFilledAnswers[pesoLinkId]);
    if (
      !Number.isNaN(alturaNum) &&
      alturaNum > 0 &&
      !Number.isNaN(pesoNum) &&
      pesoNum > 0
    ) {
      preFilledAnswers[imcLinkId] = formatImcForField(calculateImc(pesoNum, alturaNum));
    }
  }

  return preFilledAnswers;
}
